import {
  IconX,
  IconBuildingWarehouse,
  IconRoute,
  IconUser,
  IconUserCheck,
  IconTruck,
  IconCalendarClock,
  IconClipboardText,
  IconInfoCircle,
  IconPackage,
  IconHistory,
  IconPhoto,
  IconCloudDown,
  IconPhone,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetch3 } from "../../../commonApis/commonApis";
import ListView from "../../../components/common/ListView";
import NoDataFound from "../../../components/common/NoDataFound";
import formatDate, { formatDateWithTime } from "../../../utilities/helper";
import { useLanguage } from "../../../context/LanguageContext";

const url = process.env.REACT_APP_BASE_URL;

type Props = {
  open: boolean;
  onClose: () => void;
  pickup: any;
  isSidebar?: boolean;
};

const Row = ({
  icon,
  label,
  value,
  className,
}: {
  icon?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => (
  <div className={`flex items-center justify-between gap-2 py-1 min-w-0 ${className || ""}`}>
    <div className="flex items-center gap-1.5 min-w-0 flex-shrink-0">
      {icon && <div className="text-gray-400 flex-shrink-0">{icon}</div>}
      {label && (
        <span className="font-medium text-xs text-[#212121] whitespace-nowrap">
          {label}
        </span>
      )}
    </div>
    <div className="text-right min-w-0 flex-1">
      <span className="text-xs text-[#212121] font-medium truncate block" title={typeof value === "string" ? value : undefined}>
        {value || ""}
      </span>
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

type PickupRequest = {
  id?: string;
  awbNumber?: string;
  referenceNumber?: string;
  status: string;
  [key: string]: any;
};

const AsyncT: React.FC<{ text: string }> = ({ text }) => {
  const { translateText, language } = useLanguage();
  const [translated, setTranslated] = useState(text);

  useEffect(() => {
    let active = true;
    if (language === "en") {
      setTranslated(text);
      return;
    }
    translateText(text).then((res) => {
      if (active) setTranslated(res);
    });
    return () => {
      active = false;
    };
  }, [text, translateText, language]);

  return <>{translated}</>;
};

const LineHaulViewModal = ({ open, onClose, pickup, isSidebar }: Props) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<
    "OVERVIEW" | "CARGO ITEMS" | "ACTIVITY LOG" | "POD MEDIA"
  >("OVERVIEW");
  const [linehaulDetails, setLinehaulDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [openDrop, setOpenDrop] = useState<number | null>(null);

  const manifestId = pickup?.id || pickup?.manifestId || "";

  useEffect(() => {
    if (open && manifestId) {
      setLoading(true);
      fetch3(`${url}/api/linehaul-manifest/${manifestId}`, "get")
        .then((res) => {
          if (res && res.data) {
            setLinehaulDetails(res.data);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [open, manifestId]);

  if (!open || !pickup) return null;

  const details = {
    ...pickup,
    ...linehaulDetails,
  };

  const shipmentsList = details?.shipments || [];
  const statusHistories = details?.statusHistories || [];

  const handleDownload = (docUrl?: string) => {
    if (!docUrl) return;
    window.open(docUrl, "_blank");
  };

  const content = (
    <div className="space-y-2">
      {/* Tabs */}
      <div className="flex gap-2 pb-2 border-b border-gray-200/50">
        {(
          [
            {
              key: "OVERVIEW",
              icon: <IconInfoCircle size={18} />,
              label: t("Overview"),
            },
            {
              key: "CARGO ITEMS",
              icon: <IconPackage size={18} />,
              label: t("AWB"),
            },
            {
              key: "ACTIVITY LOG",
              icon: <IconHistory size={18} />,
              label: t("Timeline"),
            },
            {
              key: "POD MEDIA",
              icon: <IconPhoto size={18} />,
              label: t("POD Media"),
            },
          ] as const
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 border ${
              activeTab === tab.key
                ? "bg-[#FBD037]/20 text-[#000000] border-gray-200 shadow-sm"
                : "bg-slate-100 hover:bg-slate-200/60 text-slate-600 border-gray-200"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {loading && !linehaulDetails ? (
        <div className="flex justify-center py-12 text-sm text-gray-500 animate-pulse">
          Loading details...
        </div>
      ) : (
        <>
          {activeTab === "OVERVIEW" && (
            <div className="space-y-2">
              {/* Manifest Specifications Card */}
              {/* <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <SectionHeader icon={IconClipboardText} title="Manifest Specifications" />
                <div className="space-y-2 relative">
                  <Row label="Manifest Code" value={details.code || details.manifestId} />
                  <Row
                    label="Status"
                    value={
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                        details.status === "COMPLETED"
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-blue-50 text-blue-700 border-blue-200"
                      }`}>
                        {(details.status || "").replace(/_/g, " ")}
                      </span>
                    }
                  />
                  <Row label="Type" value="Linehaul Manifest" />
                  <Row label="Scan Code" value={details.scanCode} />
                  <Row label="Date" value={formatDate(details.createdAt || details.date)} />
                </div>
              </div> */}

              {/* Warehouse Information */}
              <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                <SectionHeader
                  icon={IconBuildingWarehouse}
                  title="Warehouse Information"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 relative">
                  <Row
                    icon={<IconBuildingWarehouse size={14} />}
                    label="From Warehouse"
                    value={details.fromWarehouse || details.fromWarehouseName}
                  />
                  <Row
                    icon={<IconBuildingWarehouse size={14} />}
                    label="To Warehouse"
                    value={details.toWarehouse || details.toWarehouseName}
                  />
                </div>
              </div>

              {/* Driver & Vehicle details */}
              {(details.driverCode ||
                details.driverName ||
                details.vehicleNumber) && (() => {
                  const assignmentHistory = statusHistories.find(
                    (h: any) => h.newStatus === "ASSIGNED" || h.newStatus === "ASSIGNED_TO_DRIVER"
                  );
                  return (
                    <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                      <SectionHeader icon={IconUserCheck} title="Driver Details" />
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 relative">
                        <Row
                          icon={<IconUserCheck size={14} />}
                          label="Driver Name"
                          value={details.driverName || details.assignedResource}
                        />
                        <Row
                          icon={<IconClipboardText size={14} />}
                          label="Driver Code"
                          value={details.driverCode}
                        />
                        <Row
                          icon={<IconPhone size={14} />}
                          label="Contact"
                          value={details.driverMobile}
                        />
                        <Row
                          icon={<IconTruck size={14} />}
                          label="Vehicle No"
                          value={details.vehicleNumber}
                        />
                        <Row
                          icon={<IconTruck size={14} />}
                          label="Vehicle Type"
                          value={details.vehicleType || details.resourceType}
                        />
                        {assignmentHistory && (
                          <>
                            <Row
                              icon={<IconUser size={14} />}
                              label={t("Assigned By")}
                              value={assignmentHistory.changedByName || "—"}
                            />
                            <Row
                              icon={<IconCalendarClock size={14} />}
                              label={t("Assigned At")}
                              value={assignmentHistory.createdAt ? formatDateWithTime(assignmentHistory.createdAt) : "—"}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </div>
          )}

          {activeTab === "CARGO ITEMS" && (
            <div className="space-y-4 font-sans text-xs">
              {/* ASSOCIATED WAYBILLS SECTION */}
              <div className="space-y-3">
                {/* <SectionHeader
                  icon={IconPackage}
                  title={`Associated Waybills (${shipmentsList.length})`}
                /> */}
                {shipmentsList.length > 0 ? (
                  <div className="space-y-3">
                    {shipmentsList.map((awb: any, index: number) => {
                      const formattedStatus = (awb.status || "")
                        .toLowerCase()
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (char: string) => char.toUpperCase());

                      const getStatusClasses = (status: string) => {
                        const lower = (status || "").toLowerCase();
                        if (
                          lower.includes("delivery") ||
                          lower.includes("dispatch") ||
                          lower.includes("loaded")
                        ) {
                          return "bg-green-50 text-green-700 border-green-200";
                        } else if (
                          lower.includes("delivered") ||
                          lower.includes("completed")
                        ) {
                          return "bg-gray-50 text-gray-700 border-gray-200";
                        } else if (lower.includes("undelivered")) {
                          return "bg-amber-50 text-amber-700 border-amber-200";
                        }
                        return "bg-blue-50 text-blue-700 border-blue-200";
                      };

                      return (
                        <div
                          key={awb.id || index}
                          className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 shadow-sm hover:shadow-md space-y-2 text-xs"
                        >
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                          <div className="relative">
                            <div className="flex justify-between items-center">
                              <p className="font-bold text-sm text-blue-600">
                                {awb.awbNumber || awb.awb || "—"}
                              </p>
                              <span
                                className={`rounded-full px-2.5 py-0.5 border text-[12px] font-semibold ${getStatusClasses(awb.status)}`}
                              >
                                {formattedStatus || t("Loaded")}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="w-full p-6 border border-[#D2D2D2]/70 rounded-2xl bg-gradient-to-b from-white/90 to-white/60 shadow-sm text-center text-xs text-slate-400">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
                    <div className="relative">
                      {t("No Associated Waybills")}
                      <div className="mt-1 text-[11px] text-slate-400 font-medium">
                        {t("No raw physical shipments registered to this manifest file.")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "ACTIVITY LOG" && (
            <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-2.5 transition-all duration-300 space-y-1.5">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              {/* <SectionHeader
                icon={IconHistory}
                title="Audit Trail History Timestamps"
              /> */}
              {statusHistories.length > 0 ? (
                <div className="space-y-0 relative pl-4 border-l border-emerald-200/60 ml-2">
                  {statusHistories.map((item: any, index: number) => {
                    const isLast = index === statusHistories.length - 1;
                    return (
                      <div
                        key={item.id || index}
                        className="relative pb-6 last:pb-0"
                      >
                        {/* Dot Indicator */}
                        {isLast ? (
                          <div className="absolute -left-[24px] top-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md border border-white ring-2 ring-emerald-600/30">
                            <IconCheck size={10} stroke={4} />
                          </div>
                        ) : (
                          <div className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-2 ring-emerald-500/20 shadow-xs" />
                        )}
                        <div>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-semibold text-slate-800">
                              {t((item.newStatus || "")
                                .replace(/_/g, " ")
                                .toLowerCase()
                                .replace(/\b\w/g, (c: string) =>
                                  c.toUpperCase(),
                                ))}
                            </p>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {formatDateWithTime(item.createdAt)}
                            </p>
                          </div>
                          {item.details && (
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              <AsyncT text={item.details} />
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-xs text-slate-400">
                  No activity history logs found.
                </div>
              )}
            </div>
          )}

          {activeTab === "POD MEDIA" && (
            <div className="space-y-4">
              {/* Document Downloads (License, Iqama, Vehicle Doc) */}
              <div className="relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-4 shadow-sm">
                <SectionHeader
                  icon={IconPhoto}
                  title={t("Assigned Documents (POD/Verification)")}
                />
                <div className="space-y-3 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-dashed border-slate-200 pb-2">
                    <div className="flex items-center gap-2">
                      <IconClipboardText size={16} className="text-slate-500" />
                      <span className="font-semibold text-xs text-slate-700">
                        {t("Driver License:")}
                      </span>
                    </div>
                    {details.driverLicense ? (
                      <button
                        onClick={() => handleDownload(details.driverLicense)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-bold cursor-pointer"
                      >
                        <IconCloudDown size={16} /> {t("Download Document")}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400 italic">
                        {t("Not Uploaded")}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-dashed border-slate-200 pb-2">
                    <div className="flex items-center gap-2">
                      <IconClipboardText size={16} className="text-slate-500" />
                      <span className="font-semibold text-xs text-slate-700">
                        {t("Driver Iqama Doc:")}
                      </span>
                    </div>
                    {details.driverIqmaDoc ? (
                      <button
                        onClick={() => handleDownload(details.driverIqmaDoc)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-bold cursor-pointer"
                      >
                        <IconCloudDown size={16} /> {t("Download Document")}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400 italic">
                        {t("Not Uploaded")}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <IconClipboardText size={16} className="text-slate-500" />
                      <span className="font-semibold text-xs text-slate-700">
                        {t("Vehicle Registration Doc:")}
                      </span>
                    </div>
                    {details.vehicleDoc ? (
                      <button
                        onClick={() => handleDownload(details.vehicleDoc)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-bold cursor-pointer"
                      >
                        <IconCloudDown size={16} /> {t("Download Document")}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400 italic">
                        {t("Not Uploaded")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Signature / Photo */}
              <div className="rounded-2xl border border-[#D2D2D2]/70 bg-gradient-to-b from-white/90 to-white/60 p-4 shadow-sm">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center border-b pb-2">
                  {t("Proof of Delivery verification photo")}
                </h4>
                <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  {details.image ? (
                    <div className="flex flex-col items-center gap-3">
                      <img
                        src={details.image}
                        alt="Proof of Delivery"
                        className="max-h-40 object-contain rounded-lg border border-slate-200 bg-white p-1"
                      />
                      <button
                        onClick={() => handleDownload(details.image)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        <IconCloudDown size={16} /> {t("Download POD Photo")}
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400 flex flex-col items-center gap-2">
                      <IconPhoto
                        size={32}
                        className="opacity-30 text-[#212121]"
                      />
                      <p className="text-xs italic">{t("No POD media available")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (isSidebar) {
    return <div className="px-6 py-2">{content}</div>;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="rounded-3xl w-full md:w-10/12 lg:w-9/12 bg-white flex flex-col max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <h1 className="text-xl font-bold text-gray-800">
            View Request: {details.customId || details.manifestId}
          </h1>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{content}</div>
      </div>
    </div>
  );
};

export default LineHaulViewModal;
